/**
 * Build the chat prompt for "+ Add tile" requests.
 *
 * The slot collects a natural-language description from the user and
 * an insertion index (where in the children array the new tile
 * should go). Rather than have the agent regenerate the whole spec,
 * we phrase the request as a yaml-edit delta — backend dash.md
 * already teaches the agent that fence — so the response arrives as
 * a small JSON Merge Patch inserting one element at the requested
 * index.
 *
 * Pure: no React, no fetch. Tested separately so the prompt-building
 * stays predictable as the dialog UI evolves.
 */

export interface AddTilePromptInput {
  /** User's description of the tile they want. Must be non-empty. */
  description: string
  /** Insertion index in the spec.children array. */
  insertionIndex: number
  /** Total existing children count — surfaces "at the end" vs "before tile N". */
  childrenCount: number
}

/**
 * Compose the chat message the user submits behind the scenes when
 * they click "Generate" on the Add Tile dialog. The resulting string
 * is what the parent dispatches into the chat pipeline.
 *
 * Returns ``null`` for invalid input (empty description, negative
 * index, index past childrenCount + 1).
 *
 * Why ``yaml-patch`` instead of ``yaml-edit``: the merge variant
 * (RFC 7396) treats ``null`` (or YAML ``~``) as DELETE in deep-merge
 * semantics, and there is no clean way to express "insert at index N
 * without touching the other slots" — earlier prototypes had the
 * agent emit ``children: [~, ~, <new>]`` which the renderer
 * correctly interpreted as "delete tiles 0 and 1, set tile 2". A
 * single ``yaml-patch`` ``add`` operation at ``/children/<index>``
 * is exactly the splice we want, and the agent's existing prompt
 * already documents that fence.
 */
export function buildAddTilePrompt(input: AddTilePromptInput): string | null {
  const description = input.description.trim()
  if (!description) return null
  if (input.insertionIndex < 0) return null
  if (input.insertionIndex > input.childrenCount) return null

  const placement = describePlacement(input.insertionIndex, input.childrenCount)
  // RFC 6902 uses "/-" to mean "append" (any path component "-"
  // refers to the array's end), which makes intent unambiguous to
  // the agent vs hard-coding the current length as an index.
  const patchPath =
    input.insertionIndex >= input.childrenCount
      ? "/children/-"
      : `/children/${input.insertionIndex}`

  return [
    `Add ONE new tile to the dashboard, ${placement}.`,
    `User description: ${description}`,
    "",
    "STEP 1 — Choose the tile's data source:",
    "",
    "A) CONNECTED DATA — if the tile is derivable from this dashboard's " +
      "existing queryBindings or the active data-connection catalog " +
      "(a SQL query, a derived KPI, a filter wired to existing state, or " +
      "a literal Text/Heading the user explicitly asked for) → build a " +
      "SQL-backed tile (see the SQL example below).",
    "",
    "B) WEB / AI RESEARCH — if the request needs current public " +
      "information that lives on the web rather than in a connection " +
      "(news, weather, market/stock info, public statistics, company " +
      "facts, etc.) → build a `ResearchSummary` tile with an inline " +
      "`_researchPrompt` hint. On every refresh the platform runs that " +
      "prompt through web search + AI synthesis and fills the tile's " +
      "`content` + `sources`. See the web-research example below.",
    "",
    "C) UPLOADED SESSION FILE — if there is no live data connection but " +
      "the dashboard was built from an uploaded Excel/CSV/document, use the " +
      "available file-search or code-interpreter tools to inspect the COMPLETE " +
      "persisted session file. Compute the requested result from the complete " +
      "dataset and emit a normal tile with static inline `data`, `rows`, or " +
      "`value`. Never derive totals from a small Table sample already embedded " +
      "in the rendered dashboard, and do not add SQL binding hints for a static tile.",
    "",
    "D) NONE OF THE ABOVE — only if the request is genuinely impossible (not a " +
      "data question at all, or self-contradictory) → DO NOT emit a " +
      "yaml-patch; reply in plain prose explaining why and suggest 1–3 " +
      "concrete alternatives.",
    "",
    "STEP 2 — For A, B, or C, output the change as a " +
      "```yaml-patch fenced code block containing exactly ONE RFC 6902 " +
      "`add` operation. Format:",
    "",
    "```yaml-patch",
    "- op: add",
    `  path: ${patchPath}`,
    "  value:",
    "    type: <ComponentType>",
    "    title: <human title>",
    "    # ...remaining tile fields per the component catalog",
    "```",
    "",
    "Pick the most appropriate component type (BarChart, LineChart, " +
      "KPICard, Table, Text, etc.) based on the description and any " +
      "matching schema you see in the data connection catalog. Do not " +
      "emit operations on other indices, and do not include placeholder " +
      "entries for the existing tiles.",
    "",
    "For SQL-backed tiles, embed the binding INLINE on the tile via " +
      "`_sql`, `_connectionId`, and `_targetField` fields — same pattern " +
      "as new-dashboard generation. The save path extracts them into " +
      "query_bindings automatically; no separate `dash_save_binding` call " +
      "needed. Example:",
    "",
    "```yaml-patch",
    "- op: add",
    `  path: ${patchPath}`,
    "  value:",
    "    type: BarChart",
    "    title: Intäkter per region",
    "    data: []",
    "    xKey: region",
    "    bars:",
    "      - dataKey: revenue",
    '        color: "#4f46e5"',
    "        label: Intäkt",
    "    _sql: |",
    "      SELECT region, SUM(amount) AS revenue FROM sales GROUP BY region",
    "    _connectionId: <exact active connection id from the system prompt>",
    "    # Never copy an example connection id; use the active connection.",
    "    _targetField: data",
    "```",
    "",
    "After the patch lands, the new tile's binding refreshes from the " +
      "connection in seconds — no agent loop needed.",
    "",
    "For WEB / AI-RESEARCH tiles, emit a `ResearchSummary` tile carrying " +
      "an inline `_researchPrompt` — a clear, self-contained research " +
      "question. Leave `content` empty; the refresh pipeline fills it. " +
      "Example:",
    "",
    "```yaml-patch",
    "- op: add",
    `  path: ${patchPath}`,
    "  value:",
    "    type: ResearchSummary",
    "    title: Nyheter i Uppsala idag",
    '    content: ""',
    "    _researchPrompt: |",
    "      Summarise today's most notable news from Uppsala, Sweden.",
    "```",
    "",
    "DO NOT use a Text tile as a fallback. A Text tile that just repeats " +
      "the user's description back is worse than no tile — for live web " +
      "topics use a ResearchSummary tile so the data is real and refreshable.",
  ].join("\n")
}

function describePlacement(index: number, total: number): string {
  if (total === 0 || index >= total) return "at the end of the dashboard"
  if (index === 0) return "at the very top, before all existing tiles"
  return `at position ${index + 1} (so it appears before the current tile #${index + 1})`
}
